// src/pages/ViecNha.js
import React, { useState, useEffect, useCallback } from "react";
import { fetchData, addData, updateData, deleteData } from "../services/api";
import { veDangNgay } from "../utils/ngay";
import { useThongBao, ThongBao, DangTai, docQuyen, hoiXoa, KhoiGon } from "../vo";

// ============================================================
// 🧹 VIỆC NHÀ
// ------------------------------------------------------------
// Theo Chân dung bản 3: "Việc nhà chung — chung quá. Việc nhà thì phải
// phân công cụ thể người làm và theo dõi kết quả."
//
// Chữ "chung" đang làm hỏng chính mục này: việc nhà mà "chung" thì đúng
// là "ai cũng nghĩ người khác làm". Nay MỌI việc đều có tên một người
// phụ trách — người duy nhất được tính vào thang đo, và là người bấm
// "Đã làm".
//
// ⚠️ Nút "✔ Đã làm" ở đây KHÁC HẲN nút "🔕 Tắt nhắc" ở khung nhắc nhở.
// Tắt nhắc = "đừng nhắc nữa". Đã làm = "công việc đã được thực hiện",
// có ghi tên người làm và giờ phút. Đừng gộp hai khái niệm này.
// ============================================================

const homNay = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const veNgay = (v) => (v ? new Date(v).toLocaleDateString("vi-VN") : "");

const veGioPhut = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return d.toLocaleString("vi-VN", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

/** Xếp một việc vào nhóm nào */
function xepNhom(viec) {
  if (viec.done) return "xong";
  if (!viec.due_date) return "chua_han";
  const han = new Date(veDangNgay(viec.due_date) + "T00:00:00");
  const nay = homNay();
  if (han < nay) return "qua_han";
  if (han.getTime() === nay.getTime()) return "hom_nay";
  return "sap_toi";
}

const soNgayTre = (viec) => {
  if (!viec.due_date) return 0;
  const han = new Date(veDangNgay(viec.due_date) + "T00:00:00");
  return Math.round((homNay() - han) / 86400000);
};

// ============================================================
// ⚠️⚠️ HAI COMPONENT DƯỚI ĐÂY PHẢI NẰM NGOÀI HÀM ViecNha ⚠️⚠️
// ------------------------------------------------------------
// Trước 07/09/2026 chúng nằm BÊN TRONG hàm ViecNha. Hàm nằm trong hàm thì
// mỗi lần vẽ lại nó là một hàm MỚI; React so sánh bằng danh tính hàm, thấy
// khác nên nó THÁO HẲN dòng cũ ra rồi DỰNG DÒNG MỚI thay vì cập nhật.
//
// Hậu quả: gõ MỘT chữ vào ô ghi lý do là cả danh sách việc bị tháo và dựng
// lại; ô nhập bị hủy giữa chừng nên mất con trỏ, rồi `autoFocus` kéo con trỏ
// về đầu ô. Ở trang Đi chợ — nơi một dòng có HAI ô nhập — lỗi này lộ ra rất
// rõ: gõ vào ô ghi chú thì chữ nhảy lên ô tên món. Chủ nhà gặp ngay hôm
// deploy Đợt 5A.
//
// Ngoài lỗi con trỏ, nó còn làm màn hình chậm dần khi danh sách dài ra.
//
// ⚠️ Bộ thử tự động KHÔNG bắt được lỗi này vì nó gán giá trị thẳng vào ô
// bằng lệnh chứ không đi qua bàn phím. Muốn thử phải gõ phím thật.
// ============================================================

// ---------- Một dòng việc ----------
function DongViec({
v,
laAdmin,
user,
coTheXoa,
dangGhiLyDo,
lyDoTam,
onDoiLyDoTam,
onMoGhiLyDo,
onHuyGhiLyDo,
onLuuLyDo,
onBaoDaLam,
onBoDanhDau,
onMoSua,
onXoaViec,
}) {
  const laNguoiPhuTrach = v.nguoi_phu_trach === user.id;
  const baoDuoc = laNguoiPhuTrach || laAdmin;
  const tre = soNgayTre(v);

  return (
    <li className="list-item viec-dong">
      <div className="viec-noi-dung">
        <div className={v.done ? "viec-ten-xong" : "viec-ten"}>{v.name}</div>
        <div className="viec-phu">
          👤 {v.ten_nguoi_phu_trach || "chưa nhờ ai"}
          {/* N8 — người cùng làm (4.2): được ghi nhận, không chịu trách nhiệm chính */}
          {Array.isArray(v.nguoi_cung_lam) && v.nguoi_cung_lam.length > 0 && (
            <> · 👥 cùng làm: {v.nguoi_cung_lam.map((n) => n.full_name).join(", ")}</>
          )}
          {v.due_date && <> · hạn {veNgay(v.due_date)}</>}
          {!v.done && tre > 0 && (
            <span className="viec-tre"> · trễ {tre} ngày</span>
          )}
          {v.done && v.completed_at && (
            <span className="viec-xong-luc">
              {" "}· {v.ten_nguoi_lam_xong} làm lúc {veGioPhut(v.completed_at)}
            </span>
          )}
        </div>
        {v.ly_do && !v.done && (
          <div className="viec-ly-do">💬 {v.ly_do}</div>
        )}

        {dangGhiLyDo === v.id && (
          <div className="viec-o-ly-do">
            <input
              type="text"
              value={lyDoTam}
              onChange={(e) => onDoiLyDoTam(e.target.value)}
              placeholder="Vì sao chưa làm được?"
              autoFocus
            />
            <button onClick={onLuuLyDo} className="nut nut-xanh">Lưu</button>
            <button onClick={onHuyGhiLyDo} className="nut nut-xam">Thôi</button>
          </div>
        )}
      </div>

      <div className="viec-nut">
        {!v.done && baoDuoc && (
          <button onClick={onBaoDaLam} className="nut nut-xanh" title="Báo là đã làm xong việc này">
            ✔ Đã làm
          </button>
        )}
        {!v.done && baoDuoc && dangGhiLyDo !== v.id && (
          <button
            onClick={onMoGhiLyDo}
            className="nut nut-xam"
            title="Ghi lý do chưa làm được"
          >
            💬
          </button>
        )}
        {v.done && baoDuoc && (
          <button onClick={onBoDanhDau} className="nut nut-xam" title="Bấm nhầm? Bỏ đánh dấu">
            ↩
          </button>
        )}
        {/* Chỉ NGƯỜI ĐÃ NHỜ (hoặc admin) mới sửa được. Người được nhờ không
            sửa được việc của mình — nếu tự dời được hạn thì việc theo dõi
            kết quả thành trò diễn. Họ có tiếng nói qua ô ghi lý do. */}
        {(laAdmin || v.user_id === user.id) && !v.done && (
          <button onClick={onMoSua} className="btn-edit">✏️</button>
        )}
        {coTheXoa && (
          <button onClick={onXoaViec} className="btn-delete">🗑️</button>
        )}
      </div>
    </li>
);
}

function Khoi({ tieuDe, ds, lop, veDong }) {
if (ds.length === 0) return null;
return (
  <div className={`viec-khoi ${lop || ""}`}>
    <h4>
      {tieuDe} ({ds.length})
    </h4>
    <ul className="list">{ds.map(veDong)}</ul>
  </div>
);
}


function ViecNha({ user, onDoiViec }) {
  const [dsViec, setDsViec] = useState([]);
  const [thanhVien, setThanhVien] = useState([]);
  const [dangTai, setDangTai] = useState(true);

  const [hienXong, setHienXong] = useState(false);
  const [hienToiNho, setHienToiNho] = useState(false);
  const [hienKhac, setHienKhac] = useState(false);
  const [hienCungLam, setHienCungLam] = useState(true);
  const [dangGhiLyDo, setDangGhiLyDo] = useState(null);
  const [lyDoTam, setLyDoTam] = useState("");

  // Biểu mẫu giao việc
  const [moForm, setMoForm] = useState(false);
  const [suaId, setSuaId] = useState(null);
  const [ten, setTen] = useState("");
  const [phuTrach, setPhuTrach] = useState("");
  const [han, setHan] = useState("");
  const [nhac, setNhac] = useState("");
  const [cungLam, setCungLam] = useState([]); // N8 — mã người cùng làm

  const { laAdmin, them: coTheGiaoViec, xoa: coTheXoa } = docQuyen(user);
  const { bao, noiBao } = useThongBao();

  const taiLai = useCallback(async () => {
    try {
      const [viec, tv] = await Promise.all([
        fetchData("viec"),
        fetchData("thanh-vien"),
      ]);
      setDsViec(viec);
      setThanhVien(tv);
      // Báo lên App để con số trên thanh điều hướng đếm lại.
      // Đặt ở đây — chỗ DUY NHẤT nạp lại danh sách — nên mọi thao tác
      // (nhờ thêm, báo đã làm, bỏ đánh dấu, xóa) đều tự động cập nhật con số,
      // không phải nhớ gọi ở từng nút một.
      if (onDoiViec) onDoiViec();
    } catch (err) {
      noiBao("⚠️ Không tải được danh sách việc nhà");
    } finally {
      setDangTai(false);
    }
  // `noiBao` khai được vào đây vì nó được ghi nhớ trong useThongBao
  // (useCallback). Thời bản cũ nó là hàm mới mỗi lần vẽ, khai vào đây là
  // tải lại danh sách vô tận.
  }, [onDoiViec, noiBao]);

  useEffect(() => {
    taiLai();
  }, [taiLai]);

  const donForm = () => {
    setTen(""); setPhuTrach(""); setHan(""); setNhac(""); setCungLam([]);
    setSuaId(null); setMoForm(false);
  };

  const luuViec = async (e) => {
    e.preventDefault();
    if (!ten.trim()) return noiBao("⚠️ Chưa có tên việc");
    if (!phuTrach) return noiBao("⚠️ Chưa chọn nhờ ai làm việc này");
    const duLieu = {
      name: ten.trim(),
      nguoi_phu_trach: Number(phuTrach),
      due_date: han || null,
      reminder: nhac || null,
      // Người phụ trách không đồng thời là người cùng làm (máy chủ cũng lọc).
      nguoi_cung_lam: cungLam.filter((id) => id !== Number(phuTrach)),
    };
    try {
      if (suaId) await updateData("viec", suaId, duLieu);
      else await addData("viec", duLieu);
      donForm();
      taiLai();
      const ten_nguoi = thanhVien.find((t) => t.id === Number(phuTrach));
      noiBao(suaId ? "✅ Đã cập nhật" : `✅ Đã nhờ ${ten_nguoi ? ten_nguoi.full_name : ""}`);
    } catch (err) {
      noiBao("⚠️ Không lưu được");
    }
  };

  const moSua = (v) => {
    setSuaId(v.id);
    setTen(v.name || "");
    setPhuTrach(String(v.nguoi_phu_trach || ""));
    setHan(veDangNgay(v.due_date));
    setNhac(veDangNgay(v.reminder));
    setCungLam(Array.isArray(v.nguoi_cung_lam) ? v.nguoi_cung_lam.map((n) => n.id) : []);
    setMoForm(true);
  };

  const baoDaLam = async (v) => {
    try {
      await updateData("viec", `${v.id}/xong`, {});
      taiLai();
      noiBao(`✅ Đã ghi nhận: ${v.name}`);
    } catch (err) {
      noiBao("⚠️ Không báo được — chỉ người được nhờ mới báo được");
    }
  };

  const boDanhDau = async (v) => {
    try {
      await updateData("viec", `${v.id}/chua-xong`, {});
      taiLai();
    } catch (err) {
      noiBao("⚠️ Không bỏ được");
    }
  };

  const luuLyDo = async (v) => {
    try {
      await updateData("viec", `${v.id}/ly-do`, { ly_do: lyDoTam });
      setDangGhiLyDo(null);
      setLyDoTam("");
      taiLai();
      noiBao("✅ Đã ghi lại");
    } catch (err) {
      noiBao("⚠️ Không ghi được");
    }
  };

  const xoaViec = async (v) => {
    if (!hoiXoa("việc", v.name)) return;
    try {
      await deleteData("viec", v.id);
      taiLai();
      noiBao("🗑️ Đã xóa");
    } catch (err) {
      noiBao("⚠️ Không xóa được");
    }
  };

  if (dangTai) return <DangTai gi="việc nhà" />;

  // ============================================================
  // BA VÙNG VIỆC — chủ nhà phát hiện 07/09/2026:
  // "màn hình báo đỏ ⚠️ Quá hạn (1) trong khi con số trên tab lại xám"
  // — vì việc quá hạn đó là của Mẹ, không phải của người đang đăng nhập.
  //
  // Cơ chế đếm KHÔNG sai: `GET /api/viec/dem` (server.js) đếm
  // `WHERE nguoi_phu_trach = <mình>`. Cái sai là **cách trình bày**: trang
  // trộn việc của cả nhà vào chung bốn nhóm rồi tô đỏ hết.
  //
  // Đừng "sửa" bằng cách thêm cờ "đã xem" để tắt màu đỏ — xem mục 9b
  // điểm 5: một cái nút làm con số biến mất mà việc chưa xong chính là
  // cái bẫy mục 5.7 đã đóng ("tắt lời nhắc" ≠ "đã làm xong").
  //
  // Chân dung mục 5.4: *"Thành viên thấy: việc của mình + các mục chung.
  // Không con số nào cả, không so sánh với ai."* Nên việc không dính gì
  // tới mình thì **ẩn hẳn với thành viên thường** — bày ra là thành bảng
  // so sánh. Chủ nhà vẫn thấy (thu gọn) vì còn phải sửa và xóa giúp.
  //
  // "Tôi đã nhờ người khác" KHÁC với "việc của người khác": đó là việc
  // chính mình nhờ, mục 4.2 bắt "theo dõi kết quả" — không cho xem thì
  // không theo dõi được.
  // ============================================================
  const laCuaToi = (v) => v.nguoi_phu_trach === user.id;
  const laToiNho = (v) => v.user_id === user.id && v.nguoi_phu_trach !== user.id;
  // N8 — mình chỉ là người cùng làm (không phụ trách)
  const laCungLam = (v) =>
    v.nguoi_phu_trach !== user.id &&
    Array.isArray(v.nguoi_cung_lam) && v.nguoi_cung_lam.some((n) => n.id === user.id);

  const cuaToi = [];
  const toiNho = [];
  const cungLamVoi = [];
  const cuaNguoiKhac = [];
  const daXong = [];

  dsViec.forEach((v) => {
    const cuaMinh = laCuaToi(v);
    const minhNho = laToiNho(v);
    const minhCungLam = laCungLam(v);
    // Việc không liên quan: thành viên thường không thấy (mục 5.4)
    if (!cuaMinh && !minhNho && !minhCungLam && !laAdmin) return;
    if (v.done) { daXong.push(v); return; }
    if (cuaMinh) cuaToi.push(v);
    else if (minhNho) toiNho.push(v);
    else if (minhCungLam) cungLamVoi.push(v);
    else cuaNguoiKhac.push(v);
  });

  // Chỉ việc CỦA TÔI mới chia bốn nhóm và mới được tô đỏ.
  // `cuaToi` đã lọc bỏ việc đã xong ở trên nên không còn nhánh "xong".
  const nhom = { qua_han: [], hom_nay: [], sap_toi: [], chua_han: [] };
  cuaToi.forEach((v) => nhom[xepNhom(v)].push(v));

  // Nối một dòng việc với state và các hàm của trang. Viết ở đây, không viết
  // trong DongViec — xem khối cảnh báo ở đầu file.
  const veDong = (v) => (
    <DongViec
      key={v.id}
      v={v}
      user={user}
      laAdmin={laAdmin}
      coTheXoa={coTheXoa}
      dangGhiLyDo={dangGhiLyDo}
      lyDoTam={lyDoTam}
      onDoiLyDoTam={setLyDoTam}
      onMoGhiLyDo={() => { setDangGhiLyDo(v.id); setLyDoTam(v.ly_do || ""); }}
      onHuyGhiLyDo={() => setDangGhiLyDo(null)}
      onLuuLyDo={() => luuLyDo(v)}
      onBaoDaLam={() => baoDaLam(v)}
      onBoDanhDau={() => boDanhDau(v)}
      onMoSua={() => moSua(v)}
      onXoaViec={() => xoaViec(v)}
    />
  );

  return (
    <div className="card">
      <h3>🧹 Việc nhà</h3>
      <ThongBao bao={bao} />

      {coTheGiaoViec && (
        <button onClick={() => (moForm ? donForm() : setMoForm(true))} className="btn-add">
          {moForm ? "❌ Hủy" : "➕ Nhờ một việc"}
        </button>
      )}

      {moForm && (
        <form onSubmit={luuViec} className="viec-form">
          <input
            type="text" placeholder="Việc gì?" value={ten}
            onChange={(e) => setTen(e.target.value)} className="input-full" autoFocus
          />
          {/* Trước đây hai ô ngày nằm cạnh nhau, GIỐNG HỆT nhau, chỉ phân biệt
              bằng chú thích hiện khi rê chuột — mà trên điện thoại thì không có
              rê chuột. Không ai biết ô nào là ô nào. Nay mỗi ô có nhãn riêng. */}
          <div className="viec-hang-nhap">
            <label className="viec-o-nhap">
              <span>Nhờ ai làm?</span>
              <select value={phuTrach} onChange={(e) => setPhuTrach(e.target.value)}>
                <option value="">— chọn người —</option>
                {thanhVien.map((t) => (
                  <option key={t.id} value={t.id}>{t.full_name}</option>
                ))}
              </select>
            </label>

            <label className="viec-o-nhap">
              <span>Mong xong trước ngày</span>
              <input type="date" value={han} onChange={(e) => setHan(e.target.value)} />
            </label>

            <label className="viec-o-nhap">
              <span>Nhắc vào ngày</span>
              <input type="date" value={nhac} onChange={(e) => setNhac(e.target.value)} />
            </label>
          </div>

          {/* N8 — NGƯỜI CÙNG LÀM (chân dung 4.2). Không bắt buộc. Được ghi
              nhận tên trên việc, nhưng KHÔNG bấm Đã làm, KHÔNG được tính, KHÔNG
              đếm vào số đỏ trên tab — trách nhiệm vẫn là của MỘT người. */}
          {thanhVien.filter((t) => t.id !== Number(phuTrach)).length > 0 && (
            <fieldset className="viec-cung-lam">
              <legend>Cùng làm với <span>(không bắt buộc)</span></legend>
              {thanhVien
                .filter((t) => t.id !== Number(phuTrach))
                .map((t) => (
                  <label key={t.id} className={`viec-cung-lam-chon ${cungLam.includes(t.id) ? "bat" : ""}`}>
                    <input
                      type="checkbox"
                      checked={cungLam.includes(t.id)}
                      onChange={(e) =>
                        setCungLam((ds) => (e.target.checked ? [...ds, t.id] : ds.filter((x) => x !== t.id)))
                      }
                    />
                    {t.full_name}
                  </label>
                ))}
            </fieldset>
          )}

          <p className="viec-ghi-chu-form">
            Mỗi việc chỉ ghi tên <strong>một người</strong> — để không rơi vào cảnh
            ai cũng nghĩ người khác làm. Người được nhờ có thể ghi lý do nếu chưa
            làm được.
          </p>
          <button type="submit" className="btn-add">
            {suaId ? "✅ Cập nhật" : "✅ Nhờ"}
          </button>
        </form>
      )}

      {dsViec.length === 0 && <p>Chưa có việc nào.</p>}

      {/* ─── VÙNG 1: VIỆC CỦA TÔI ───
          Màu đỏ chỉ xuất hiện trong vùng này, nên nó khớp đúng con số
          trên tab. Đừng đem khối "Quá hạn" ra ngoài vùng này. */}
      <div className="viec-vung">
        <h4 className="viec-vung-ten">👤 Việc của tôi</h4>
        {cuaToi.length === 0 ? (
          <p className="viec-vung-rong">
            🎉 Bạn không có việc nào đang chờ.
          </p>
        ) : (
          <>
            <Khoi tieuDe="⚠️ Quá hạn"   ds={nhom.qua_han} lop="khoi-qua-han" veDong={veDong} />
            <Khoi tieuDe="📌 Hôm nay"   ds={nhom.hom_nay} lop="khoi-hom-nay" veDong={veDong} />
            <Khoi tieuDe="📅 Sắp tới"   ds={nhom.sap_toi} veDong={veDong} />
            <Khoi tieuDe="🕒 Chưa đặt hạn" ds={nhom.chua_han} veDong={veDong} />
          </>
        )}
      </div>

      {/* ─── N8: VIỆC MÌNH CÙNG LÀM ───
          Không tô đỏ, không có nút Đã làm, không đếm vào số trên tab — người
          phụ trách mới là người chịu trách nhiệm (chân dung 4.2). */}
      <KhoiGon
        tieuDe="👥 Cùng làm với người khác"
        ghiChu="Bạn được ghi tên cùng làm. Người phụ trách (👤) là người bấm Đã làm."
        ds={cungLamVoi}
        veDong={veDong}
        mo={hienCungLam}
        onDoiMo={() => setHienCungLam(!hienCungLam)}
      />

      {/* ─── VÙNG 2: TÔI ĐÃ NHỜ NGƯỜI KHÁC ───
          Danh sách phẳng, KHÔNG chia Quá hạn/Hôm nay — chia ra là lại có thêm
          một dàn tiêu đề đỏ nữa, đúng cái vừa đi sửa. Mỗi dòng vẫn tự ghi
          "trễ N ngày" bằng chữ trong `.viec-phu`. */}
      <KhoiGon
        tieuDe="🤝 Tôi đã nhờ người khác"
        ghiChu="Việc bạn nhờ, người khác làm. Bạn sửa hoặc xóa được, nhưng người được nhờ mới là người bấm Đã làm."
        ds={toiNho}
        veDong={veDong}
        mo={hienToiNho}
        onDoiMo={() => setHienToiNho(!hienToiNho)}
      />

      {/* ─── VÙNG 3: VIỆC CỦA NGƯỜI KHÁC — CHỈ CHỦ NHÀ ───
          `cuaNguoiKhac` đã luôn rỗng với thành viên thường (lọc ở trên), nên
          KhoiGon tự ẩn. Đừng bỏ lớp lọc đó đi rồi chỉ dựa vào điều kiện ở đây. */}
      <KhoiGon
        tieuDe="🏠 Việc của người khác trong nhà"
        ghiChu="Chỉ chủ nhà thấy mục này, để sửa giúp khi cần. Đây không phải bảng theo dõi ai siêng ai lười."
        ds={cuaNguoiKhac}
        veDong={veDong}
        mo={hienKhac}
        onDoiMo={() => setHienKhac(!hienKhac)}
      />

      <KhoiGon
        tieuDe="✅ Đã xong"
        ds={daXong}
        veDong={veDong}
        mo={hienXong}
        onDoiMo={() => setHienXong(!hienXong)}
      />
    </div>
  );
}

export default ViecNha;
